import { useEffect, useState } from "react";
import type { AccessUiMessageRecord } from "../api/adminAccessUiApi";

type AccessUiMessageModalProps = {
  open: boolean;
  initial: AccessUiMessageRecord | null;
  onClose: () => void;
  onSave: (payload: AccessUiMessageRecord) => void;
  saving?: boolean;
  allowReasonEdit?: boolean;
};

export default function AccessUiMessageModal({
  open,
  initial,
  onClose,
  onSave,
  saving = false,
  allowReasonEdit = false,
}: AccessUiMessageModalProps) {
  const [formState, setFormState] = useState<AccessUiMessageRecord | null>(initial);

  useEffect(() => {
    setFormState(initial);
  }, [initial]);

  if (!open || !formState) {
    return null;
  }

  const updateField = <K extends keyof AccessUiMessageRecord>(key: K, value: AccessUiMessageRecord[K]) => {
    setFormState((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, [key]: value };
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState) {
      return;
    }
    onSave(formState);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Access UI Mesajı</p>
            <h2 className="text-lg font-semibold text-slate-900">{formState.reason}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
            aria-label="Kapat"
          >
            ×
          </button>
        </header>
        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto px-6 py-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-600">
              Reason Kodu
              <input
                type="text"
                value={formState.reason}
                onChange={(event) => updateField("reason", event.target.value)}
                disabled={!allowReasonEdit}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
                placeholder="örn. quota_exhausted"
              />
            </label>
            <label className="text-sm font-medium text-slate-600">
              Başlık
              <input
                type="text"
                value={formState.title}
                onChange={(event) => updateField("title", event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                placeholder="Kart başlığı"
              />
            </label>
          </div>

          <label className="mt-4 block text-sm font-medium text-slate-600">
            Mesaj metni
            <textarea
              value={formState.message}
              onChange={(event) => updateField("message", event.target.value)}
              className="mt-1 h-28 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
              placeholder="Kullanıcıya gösterilecek açıklamayı yazın."
            />
          </label>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="space-y-2 rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Birincil CTA</p>
              <label className="text-sm text-slate-600">
                Metin
                <input
                  type="text"
                  value={formState.primaryCtaLabel ?? ""}
                  onChange={(event) => updateField("primaryCtaLabel", event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                  placeholder="Örn. Paketleri incele"
                />
              </label>
              <label className="text-sm text-slate-600">
                URL
                <input
                  type="text"
                  value={formState.primaryCtaUrl ?? ""}
                  onChange={(event) => updateField("primaryCtaUrl", event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                  placeholder="/packages"
                />
              </label>
            </div>

            <div className="space-y-2 rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">İkincil CTA</p>
              <label className="text-sm text-slate-600">
                Metin
                <input
                  type="text"
                  value={formState.secondaryCtaLabel ?? ""}
                  onChange={(event) => updateField("secondaryCtaLabel", event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                  placeholder="Örn. Tekli aç"
                />
              </label>
              <label className="text-sm text-slate-600">
                URL
                <input
                  type="text"
                  value={formState.secondaryCtaUrl ?? ""}
                  onChange={(event) => updateField("secondaryCtaUrl", event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                  placeholder="/packages#single-unlock"
                />
              </label>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!formState.showPackages}
                onChange={(event) => updateField("showPackages", event.target.checked)}
              />
              Paket CTA
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!formState.showSingleUnlock}
                onChange={(event) => updateField("showSingleUnlock", event.target.checked)}
              />
              Tekli açma kartı
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!formState.showLoginPrompt}
                onChange={(event) => updateField("showLoginPrompt", event.target.checked)}
              />
              Giriş çağrısı
            </label>
          </div>

          <div className="mt-6 flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-emerald-300"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
